import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useOrgFilter } from "./OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";

const DmSocketContext = createContext(null);

/**
 * Single Socket.IO connection for intra-org DMs, keyed by selectedOrgId.
 * Rejoins room on org switch; disconnects when Personal/social is selected.
 */
export function DmSocketProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const [presenceByUserId, setPresenceByUserId] = useState(() => new Map());

  const orgId =
    selectedOrgId && selectedOrgId !== "social" ? String(selectedOrgId) : null;

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const emitLocal = useCallback((event, payload) => {
    for (const handler of listenersRef.current) {
      try {
        handler(event, payload);
      } catch (err) {
        console.error("DM socket listener error:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !orgId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      setPresenceByUserId(new Map());
      return undefined;
    }

    const socket = io(`${API_BASE_URL}/dm`, {
      auth: (cb) => cb({ token: getAccessToken() }),
      transports: ["websocket", "polling"],
      withCredentials: true,
    });
    socketRef.current = socket;

    const onConnect = () => {
      setConnected(true);
      socket.emit("dm.join", { org_id: orgId });
    };
    const onDisconnect = () => setConnected(false);
    const onMessage = (payload) => emitLocal("dm.message", payload);
    const onRead = (payload) => emitLocal("dm.read", payload);
    const onUpdated = (payload) => emitLocal("dm.message.updated", payload);
    const onDeleted = (payload) => emitLocal("dm.message.deleted", payload);
    const onConvDeleted = (payload) =>
      emitLocal("dm.conversation.deleted", payload);
    const onReaction = (payload) => emitLocal("dm.message.reaction", payload);
    const onTyping = (payload) => emitLocal("dm.typing", payload);
    const onConvUpdated = (payload) =>
      emitLocal("dm.conversation.updated", payload);
    const onPresence = (payload) => {
      if (!payload?.user_id) return;
      setPresenceByUserId((prev) => {
        const next = new Map(prev);
        next.set(payload.user_id, {
          online: !!payload.online,
          presence_status: payload.presence_status || null,
        });
        return next;
      });
      emitLocal("presence.updated", payload);
    };
    const onPresenceSnapshot = (payloads) => {
      if (!Array.isArray(payloads)) return;
      setPresenceByUserId((prev) => {
        const next = new Map(prev);
        payloads.forEach((payload) => {
          if (!payload?.user_id) return;
          next.set(payload.user_id, {
            online: !!payload.online,
            presence_status: payload.presence_status || null,
          });
        });
        return next;
      });
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("dm.message", onMessage);
    socket.on("dm.read", onRead);
    socket.on("dm.message.updated", onUpdated);
    socket.on("dm.message.deleted", onDeleted);
    socket.on("dm.conversation.deleted", onConvDeleted);
    socket.on("dm.conversation.updated", onConvUpdated);
    socket.on("dm.message.reaction", onReaction);
    socket.on("dm.typing", onTyping);
    socket.on("presence.updated", onPresence);
    socket.on("presence.snapshot", onPresenceSnapshot);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("dm.message", onMessage);
      socket.off("dm.read", onRead);
      socket.off("dm.message.updated", onUpdated);
      socket.off("dm.message.deleted", onDeleted);
      socket.off("dm.conversation.deleted", onConvDeleted);
      socket.off("dm.conversation.updated", onConvUpdated);
      socket.off("dm.message.reaction", onReaction);
      socket.off("dm.typing", onTyping);
      socket.off("presence.updated", onPresence);
      socket.off("presence.snapshot", onPresenceSnapshot);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      setConnected(false);
    };
  }, [isAuthenticated, orgId, emitLocal]);

  const emitTyping = useCallback(
    (conversationId, typing) => {
      const socket = socketRef.current;
      if (!socket || !orgId || conversationId == null) return;
      socket.emit("dm.typing", {
        org_id: orgId,
        conversation_id: Number(conversationId),
        typing: !!typing,
      });
    },
    [orgId]
  );

  const value = {
    connected,
    orgId,
    subscribe,
    emitTyping,
    presenceByUserId,
  };

  return (
    <DmSocketContext.Provider value={value}>{children}</DmSocketContext.Provider>
  );
}

export function useDmSocket() {
  const ctx = useContext(DmSocketContext);
  if (!ctx) {
    throw new Error("useDmSocket must be used within DmSocketProvider");
  }
  return ctx;
}

/** Presence-aware components may also render in isolated tests/previews. */
export function useOptionalDmSocket() {
  return useContext(DmSocketContext);
}
